import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MotionConfig, motion } from "framer-motion";
import { businesses } from "./thtData";
import { Reveal, SectionHeading, Marquee, FloatingDecor, GroundEdge, GrowSprout, GrowFlower, PageMeta } from "./ThtParts";
import { isLoaderActive, LOADER_ENDING_EVENT } from "./ThtLayout";
import "./ThtTop.css";
import "./ThtDesign.css";
import "./ThtNatural.css";

// ページ再読み込みでリセット、SPA内遷移では維持
let heroAnimationPlayed = false;

// 事業ごとのアクセントカラー(MTTキャラクターの色)
const businessStyles = [
  { color: "#5cae5a", en: "Sports Club", anchor: "sports" },
  { color: "#f2b632", en: "Competition", anchor: "mtt" },
  { color: "#4a90c8", en: "Learning", anchor: "juku" },
];

// ブラウザのタブに表示するページ名と、検索結果に出る説明
const pageMeta = {
  title: "THT合同会社｜好奇心のタネをまく",
  description: "THT合同会社は、体を動かすことも学ぶことも「成長のタネ」として、子どもたちがそれぞれのペースで芽を出せる場所を地域につくっています。",
  canonical: "https://tht-group.co.jp/",
};

const ThtTop = () => {
  const [playDrop, setPlayDrop] = useState(false);
  // 読み込み時の演出が再生中は、演出が開き始めるまで「好奇心のタネをまく」の文字を出さない
  const [heroReady, setHeroReady] = useState(() => !isLoaderActive());

  useEffect(() => {
    if (heroAnimationPlayed) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    // 読み込み時の演出が再生中なら、演出が終わる直前に合わせて手を下ろし始める
    if (isLoaderActive()) {
      const start = () => {
        setHeroReady(true);
        heroAnimationPlayed = true;
        setPlayDrop(true);
      };
      window.addEventListener(LOADER_ENDING_EVENT, start, { once: true });
      return () => window.removeEventListener(LOADER_ENDING_EVENT, start);
    }
    heroAnimationPlayed = true;
    setPlayDrop(true);
  }, []);

  return (
    <>
      <PageMeta {...pageMeta} />
    <MotionConfig reducedMotion="user">
      <div className="tht-home">
        {/* ヒーロー:下端は丘の形、芽が生えてくる */}
        <section className="hero">
          <div className="hero-scene">
            <img className="hero-ground" src="/images/home-hero-ground.jpg" alt="空の下、土の上で遊ぶ子どもたち" />
            <img
              className={`hero-hand${playDrop ? " hero-hand-drop" : ""}`}
              src="/images/home-hero-hand.png"
              alt="種をまく手"
            />
            <motion.img
              className="hero-text-image"
              src="/images/home-hero-text.png"
              alt="好奇心のタネをまく"
              initial={{ opacity: 0, scale: 1.08 }}
              animate={heroReady ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 1.08 }}
              transition={{ duration: 1.2, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            />
          </div>
          <GroundEdge color="#ffffff" className="hero-ground-edge" />
          <div className="hero-scroll" aria-hidden="true">
            <span className="hero-scroll-seed" />
            <span>SCROLL</span>
          </div>
        </section>

        {/* メッセージ */}
        <section className="home-message">
          <FloatingDecor />
          <div className="content-container home-message-inner">
            <Reveal className="home-message-catch">
              <span className="home-message-en">Our Mission</span>
              <p>
                誰もが成長できる。
                <br />
                場所をつくる。
              </p>
            </Reveal>
            <Reveal delay={0.15} className="home-message-text">
              <p>
                人はそれぞれ、違うスピードで、違う形で育っていきます。
                私たちは一人ひとりの「今」に種をまき、
                それぞれのペースで育っていける場所を、地域の中につくり続けます。
              </p>
              <Link to="/company" className="tht-link-arrow">
                私たちについて <span />
              </Link>
            </Reveal>
            <GrowSprout className="home-message-sprout" />
          </div>
        </section>

        <Marquee text="Plant the seeds of curiosity ・ 好奇心のタネをまく ・ " />

        {/* 事業:上端は芽の生えた丘 */}
        <section className="home-business">
          <GroundEdge color="#fdf3ee" />
          <div className="home-business-body">
            <FloatingDecor />
            <GrowFlower className="home-business-flower" />
            <div className="content-container">
              <SectionHeading en="Business" ja="THT合同会社の事業" x={-48} />
              <Reveal delay={0.1} x={48} y={0}>
                <p className="lead-text">
                  体を動かすことも、学ぶことも、同じ「成長のタネ」として捉え、
                  <br className="pc-only" />
                  それぞれのペースで芽を出せる場所を、地域の中につくっています。
                </p>
              </Reveal>
              <div className="home-business-grid">
                {businesses.map((b, i) => (
                  <Reveal key={b.title} delay={0.12 * i} x={-48} y={0}>
                    <Link
                      to={`/business#${businessStyles[i].anchor}`}
                      className="home-business-card"
                      style={{ "--accent": businessStyles[i].color } as React.CSSProperties}
                    >
                      <span className="home-business-head">
                        <h3>{b.title}</h3>
                        <span className="home-business-chevron" aria-hidden="true" />
                      </span>
                      <p>{b.text}</p>
                    </Link>
                  </Reveal>
                ))}
              </div>
              <Reveal className="btn-row" x={48} y={0}>
                <Link to="/business" className="tht-btn btn-orange">
                  業務内容を見る <span>&rarr;</span>
                </Link>
              </Reveal>
            </div>
          </div>
        </section>

        {/* お問い合わせ導線:上端は丘の形 */}
        <section className="home-cta">
          <GroundEdge color="#e8734a" className="home-cta-edge" />
          <div className="home-cta-body">
            <Reveal className="content-container home-cta-inner" x={-48} y={0}>
              <span className="home-cta-en">Contact</span>
              <p className="home-cta-title">教室のこと、事業のこと、お気軽にご相談ください。</p>
              <Link to="/contact" className="tht-btn btn-white">
                お問い合わせ <span>&rarr;</span>
              </Link>
            </Reveal>
          </div>
        </section>
      </div>
    </MotionConfig>
    </>
  );
};

export default ThtTop;
